import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  TruckIcon,
  MapPinIcon,
  DocumentTextIcon,
  ArrowLeftIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  PencilIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { shipmentsService } from '../services/logistics/shipments';
import { shipmentCostingsService } from '../services/logistics/shipment-costings';
import { driversService } from '../services/logistics/drivers';
import { vehiclesService } from '../services/logistics/vehicles';
import { truckTypesService } from '../services/logistics/truck-types';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import BillOfLadingModal from '../components/logistics/modals/BillOfLadingModal';
import AuditHistoryButton from '../components/audit/AuditHistoryButton';
import { AUDIT_MODULES } from '../services/system/audit';
import { saudiCities } from '../data/saudiCities';

// Truck types data
const truckTypes = [
  { value: 'pickup', label: { en: 'Pickup Truck', ar: 'شاحنة صغيرة' } },
  { value: 'van', label: { en: 'Van', ar: 'فان' } },
  { value: 'box_truck', label: { en: 'Box Truck', ar: 'شاحنة صندوق' } },
  { value: 'flatbed', label: { en: 'Flatbed Truck', ar: 'شاحنة مسطحة' } },
  { value: 'refrigerated', label: { en: 'Refrigerated Truck', ar: 'شاحنة مبردة' } },
  { value: 'tanker', label: { en: 'Tanker Truck', ar: 'شاحنة صهريج' } },
  { value: 'semi_trailer', label: { en: 'Semi-Trailer', ar: 'مقطورة نصفية' } },
  { value: 'heavy_duty', label: { en: 'Heavy Duty Truck', ar: 'شاحنة ثقيلة' } }
];

const ShipmentDetail = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentCompany } = useAuth();
  const [loading, setLoading] = useState(true);
  const [shipment, setShipment] = useState(null);
  const [costing, setCosting] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [truckTypes, setTruckTypes] = useState([]);
  const [showBillOfLadingModal, setShowBillOfLadingModal] = useState(false);
  const [siblingIds, setSiblingIds] = useState([]);

  // Load the sibling shipment id list once per company so we can navigate
  // prev/next without leaving the detail page. Uses the cached getShipments.
  useEffect(() => {
    if (!currentCompany?.id) return;
    let cancelled = false;
    shipmentsService.getShipments(currentCompany.id)
      .then(list => { if (!cancelled) setSiblingIds(list.map(s => s.id)); })
      .catch(() => { /* non-fatal: nav buttons just stay disabled */ });
    return () => { cancelled = true; };
  }, [currentCompany?.id]);

  const currentIndex = siblingIds.indexOf(id);
  const prevId = currentIndex > 0 ? siblingIds[currentIndex - 1] : null;
  const nextId = currentIndex >= 0 && currentIndex < siblingIds.length - 1
    ? siblingIds[currentIndex + 1]
    : null;

  // Keyboard arrows follow visual direction: in RTL, ← means "forward/next"
  // and → means "back/previous". Ignored while typing in inputs/textareas.
  const isRTL = i18n.language?.startsWith('ar');
  useEffect(() => {
    const handler = (e) => {
      const tag = e.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target?.isContentEditable) return;
      const back = isRTL ? 'ArrowRight' : 'ArrowLeft';
      const forward = isRTL ? 'ArrowLeft' : 'ArrowRight';
      if (e.key === back && prevId) navigate(`/shipments/${prevId}`);
      else if (e.key === forward && nextId) navigate(`/shipments/${nextId}`);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [prevId, nextId, navigate, isRTL]);

  // Format date for display as DD/MM/YYYY
  const formatDateForDisplay = (date) => {
    if (!date) return '';
    const dateObj = date.toDate ? date.toDate() : new Date(date);
    const day = String(dateObj.getDate()).padStart(2, '0');
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const year = dateObj.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Helper function to get city name in current language
  const getCityName = (cityKey) => {
    if (!cityKey) return '';
    
    // Search in all cities (both major and others)
    const city = saudiCities.find(c => c.value === cityKey);
    
    if (city) {
      return city.label[i18n.language] || city.label.en;
    }
    
    return cityKey; // fallback to original key if not found
  };

  // Helper function to get driver info
  const getDriverInfo = (driverId) => {
    const driver = drivers.find(d => d.id === driverId || d.id === parseInt(driverId));
    return driver || null;
  };

  // Helper function to get vehicle info
  const getVehicleInfo = (vehicleIdOrPlate) => {
    // Try to find by ID first (both string and number)
    let vehicle = vehicles.find(v => v.id === vehicleIdOrPlate || v.id === parseInt(vehicleIdOrPlate));
    
    // If not found, try to find by plate number
    if (!vehicle) {
      vehicle = vehicles.find(v => v.plateNumber === vehicleIdOrPlate);
    }
    
    return vehicle || null;
  };

  // Helper function to get truck type name
  const getTruckTypeName = (truckTypeValue) => {
    if (!truckTypeValue) return t('common.not_specified', 'Not specified');
    
    const truckType = truckTypes.find(tt => tt.value === truckTypeValue);
    if (truckType && truckType.name) {
      return truckType.name[i18n.language] || truckType.name.en;
    }
    
    return truckTypeValue;
  };

  // Helper function to calculate total shipment price including all charges
  const calculateTotalShipmentPrice = (shipment) => {
    if (!shipment) return 0;
    
    const baseShipmentPrice = parseFloat(shipment.cost || 0);
    const backhaulPrice = parseFloat(shipment.backhaulPrice || 0);
    const itemizedTotal = (shipment.itemizedCosts || []).reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);
    
    // Calculate VAT
    let mainBasePrice, mainVatAmount;
    let backhaulVatAmount = backhaulPrice * 0.15;
    let itemizedVatAmount = itemizedTotal * 0.15;

    if (shipment.isVatIncluded) {
      // Main price includes VAT, backhaul and itemized costs are without VAT
      mainBasePrice = baseShipmentPrice / 1.15;
      mainVatAmount = baseShipmentPrice - mainBasePrice;
    } else {
      // All prices are without VAT
      mainBasePrice = baseShipmentPrice;
      mainVatAmount = baseShipmentPrice * 0.15;
    }

    const totalBasePrice = mainBasePrice + backhaulPrice + itemizedTotal;
    const totalVatAmount = mainVatAmount + backhaulVatAmount + itemizedVatAmount;
    const grandTotal = totalBasePrice + totalVatAmount;
    
    return grandTotal;
  };

  // Helper function to calculate subtotal before tax
  const calculateSubtotalBeforeTax = (shipment) => {
    if (!shipment) return 0;
    
    const baseShipmentPrice = parseFloat(shipment.cost || 0);
    const backhaulPrice = parseFloat(shipment.backhaulPrice || 0);
    const itemizedTotal = (shipment.itemizedCosts || []).reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);
    
    let mainBasePrice;

    if (shipment.isVatIncluded) {
      // Main price includes VAT, extract base price
      mainBasePrice = baseShipmentPrice / 1.15;
    } else {
      // All prices are without VAT
      mainBasePrice = baseShipmentPrice;
    }

    // Subtotal = base price + backhaul + itemized costs (all without VAT)
    const subtotal = mainBasePrice + backhaulPrice + itemizedTotal;
    
    return subtotal;
  };



  // Helper function to get shipment type display  
  const getShipmentTypeDisplay = (shipment) => {
    // For legacy shipments without shipmentType field
    if (!shipment.shipmentType) {
      const isLocal = shipment.originCity === shipment.destinationCity;
      return isLocal ? t('shipments.local_shipment') : t('shipments.outbound_shipment');
    }
    
    return shipment.shipmentType === 'local' ? t('shipments.local_shipment') : t('shipments.outbound_shipment');
  };

  // Helper function to get shipment type badge
  const getShipmentTypeBadge = (shipment) => {
    const isLocal = shipment.shipmentType === 'local' || (!shipment.shipmentType && shipment.originCity === shipment.destinationCity);
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        isLocal 
          ? 'bg-green-100 text-green-800' 
          : 'bg-orange-100 text-orange-800'
      }`}>
        {getShipmentTypeDisplay(shipment)}
      </span>
    );
  };

  // Helper function to format time with proper AM/PM format for Arabic
  const formatTimeForDisplay = (dateTimeString) => {
    if (!dateTimeString) return '';
    
    const date = new Date(dateTimeString);
    
    // Always use English format for time display regardless of language
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  useEffect(() => {
    const fetchShipment = async () => {
      if (!currentCompany?.id) {
        setLoading(false);
        return;
      }

      try {
        // Load all required data in parallel
        const [shipmentData, driversData, vehiclesData, truckTypesData, costingData] = await Promise.all([
          shipmentsService.getShipment(id, currentCompany?.id),
          driversService.getDrivers(currentCompany.id),
          vehiclesService.getVehicles(currentCompany.id),
          truckTypesService.getTruckTypes(currentCompany.id),
          shipmentCostingsService.getCostingByShipment(id).catch(() => null),
        ]);

        if (shipmentData && shipmentData.companyId === currentCompany.id) {
          setShipment(shipmentData);
          setDrivers(driversData);
          setVehicles(vehiclesData);
          setTruckTypes(truckTypesData);
          if (costingData) setCosting(costingData);
        } else {
          setShipment(null);
        }
      } catch (error) {
        console.error('Error fetching shipment:', error);
        toast.error(t('shipments.failed_to_load_details', 'Failed to load shipment details'));
        setShipment(null);
      } finally {
        setLoading(false);
      }
    };

    fetchShipment();
  }, [id, currentCompany?.id, t]);

  const getStatusBadge = (status) => {
    const badges = {
      pending: { class: 'badge-warning', icon: ClockIcon },
      in_transit: { class: 'badge-info', icon: TruckIcon },
      delivered: { class: 'badge-success', icon: CheckCircleIcon },
      canceled: { class: 'badge-danger', icon: ExclamationTriangleIcon },
    };

    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;

    return (
      <span className={`badge ${badge.class} inline-flex items-center`}>
        <Icon className="w-4 h-4 mr-1" />
        {t(`shipments.${status}`)}
      </span>
    );
  };

  const updateShipmentStatus = async (newStatus) => {
    try {
      await shipmentsService.updateShipment(id, { 
        status: newStatus,
        updatedAt: new Date()
      });
      setShipment(prev => ({ ...prev, status: newStatus }));
      toast.success(t('shipments.status_updated_success', `Shipment status updated to ${newStatus}`));
    } catch (error) {
      console.error('Error updating shipment status:', error);
      toast.error(t('shipments.status_update_failed', 'Failed to update shipment status'));
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (!shipment) {
    return (
      <div className="text-center py-12">
        <TruckIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">{t('shipments.shipment_not_found', 'Shipment not found')}</h3>
        <p className="mt-1 text-sm text-gray-500">
          {t('shipments.shipment_not_found_message', `The shipment with ID "${id}" could not be found.`)}
        </p>
        <div className="mt-6">
          <button
            onClick={() => navigate('/shipments')}
            className="btn btn-primary"
          >
            {t('shipments.back_to_shipments', 'Back to Shipments')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/shipments')}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100"
            title={t('shipments.back_to_shipments', 'Back to Shipments')}
          >
            <ArrowLeftIcon className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-1">
            <button
              onClick={() => prevId && navigate(`/shipments/${prevId}`)}
              disabled={!prevId}
              className="p-2 text-gray-400 hover:text-gray-700 rounded-md hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent"
              title={t('shipments.previous_shipment', 'Previous shipment')}
            >
              {isRTL ? <ChevronRightIcon className="w-5 h-5" /> : <ChevronLeftIcon className="w-5 h-5" />}
            </button>
            {currentIndex >= 0 && siblingIds.length > 0 && (
              <span className="text-xs text-gray-500 tabular-nums px-1" dir="ltr">
                {currentIndex + 1} / {siblingIds.length}
              </span>
            )}
            <button
              onClick={() => nextId && navigate(`/shipments/${nextId}`)}
              disabled={!nextId}
              className="p-2 text-gray-400 hover:text-gray-700 rounded-md hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent"
              title={t('shipments.next_shipment', 'Next shipment')}
            >
              {isRTL ? <ChevronLeftIcon className="w-5 h-5" /> : <ChevronRightIcon className="w-5 h-5" />}
            </button>
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {t('shipments.shipment_title', 'Shipment')} {shipment.trackingNumber}
            </h1>
            <p className="text-sm text-gray-500">
              {t('shipments.created_on', 'Created on')} {formatDateForDisplay(shipment.createdAt)}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-4">
          {getStatusBadge(shipment.status)}
          <AuditHistoryButton module={AUDIT_MODULES.TRIP} recordId={id} />
          <button
            onClick={() => setShowBillOfLadingModal(true)}
            className="btn btn-outline btn-primary inline-flex items-center"
          >
            <DocumentTextIcon className="w-4 h-4 mr-2" />
            {t('shipments.bill_of_lading', 'Bill of Lading')}
          </button>
          <button
            onClick={() => navigate(`/shipments/${id}/edit`)}
            className="btn btn-secondary inline-flex items-center"
          >
            <PencilIcon className="w-4 h-4 mr-2" />
            {t('common.edit')}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Information */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-semibold flex items-center">
                <DocumentTextIcon className="w-5 h-5 mr-2" />
                {t('shipments.shipment_information', 'Shipment Information')}
              </h3>
            </div>
            <div className="card-body">
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-600">{t('shipments.tracking_number')}</label>
                    <p className="text-lg font-semibold text-gray-900">{shipment.trackingNumber}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">{t('shipments.client')}</label>
                    <p className="text-gray-900">{shipment.clientName}</p>
                  </div>
                  {shipment.requesterName && (
                    <div>
                      <label className="text-sm font-medium text-gray-600">{t('shipments.service_requester')}</label>
                      <p className="text-gray-900">{shipment.requesterName}</p>
                    </div>
                  )}
                  <div>
                    <label className="text-sm font-medium text-gray-600">{t('shipments.service_type')}</label>
                    <p className="text-gray-900 capitalize">{t(`shipments.service_${shipment.serviceType}`, shipment.serviceType)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">{t('shipments.shipment_type')}</label>
                    <div className="mt-1">
                      {getShipmentTypeBadge(shipment)}
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">{t('shipments.priority')}</label>
                    <p className={`capitalize font-medium ${
                      shipment.priority === 'urgent' ? 'text-red-600' :
                      shipment.priority === 'high' ? 'text-orange-600' :
                      shipment.priority === 'normal' ? 'text-blue-600' : 'text-gray-600'
                    }`}>
                      {t(`shipments.priority_${shipment.priority}`, shipment.priority)}
                    </p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-600">{t('shipments.pickup_date')}</label>
                    <p className="text-gray-900">
                      {shipment.pickupDate ? formatDateForDisplay(shipment.pickupDate) : t('common.not_set')}
                    </p>
                    {shipment.pickupDate && (
                      <p className="text-sm text-gray-500" style={{ direction: 'ltr' }}>
                        {formatTimeForDisplay(shipment.pickupDate)}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">{t('shipments.expected_delivery')}</label>
                    <p className="text-gray-900">
                      {shipment.expectedDeliveryDate ? formatDateForDisplay(shipment.expectedDeliveryDate) : t('common.not_set')}
                    </p>
                    {shipment.expectedDeliveryDate && (
                      <p className="text-sm text-gray-500" style={{ direction: 'ltr' }}>
                        {formatTimeForDisplay(shipment.expectedDeliveryDate)}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">{t('shipments.driver')}</label>
                    <p className="text-gray-900">
                      {(() => {
                        if (shipment.driverId) {
                          const driver = getDriverInfo(shipment.driverId);
                          if (driver) {
                            return driver.nickname && driver.nickname.trim() ? driver.nickname : driver.name;
                          }
                        }
                        return shipment.driverName && shipment.driverName !== 'Unknown Driver' 
                          ? shipment.driverName 
                          : t('common.not_assigned', 'Not assigned');
                      })()}
                    </p>
                    {shipment.isExternalDriver && (
                      <p className="text-xs text-purple-600 font-medium mt-1">
                        سائق خارجي
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">{t('shipments.vehicle')}</label>
                    <p className="text-gray-900">
                      {(() => {
                        // For external drivers, show "خارجي" (External)
                        if (shipment.isExternalDriver) {
                          return t('common.external', 'External');
                        }
                        
                        // First check if the shipment has a direct vehiclePlate
                        if (shipment.vehiclePlate && shipment.vehiclePlate !== 'Unknown Vehicle') {
                          return shipment.vehiclePlate;
                        }
                        
                        // If not, try to find the vehicle by vehicleId
                        if (shipment.vehicleId) {
                          const vehicle = getVehicleInfo(shipment.vehicleId);
                          if (vehicle) {
                            return vehicle.plateNumber;
                          }
                        }
                        
                        return t('common.not_assigned', 'Not assigned');
                      })()}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>



          {/* Route Information */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-semibold flex items-center">
                <MapPinIcon className="w-5 h-5 mr-2" />
                {t('shipments.route_information')}
              </h3>
            </div>
            <div className="card-body">
              {shipment.routeCities && shipment.routeCities.length > 0 ? (
                // New route system display
                <div className="space-y-4">
                  {/* Route Summary */}
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 p-6 rounded-xl shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-lg font-semibold text-gray-900 flex items-center">
                        <MapPinIcon className="w-5 h-5 mr-2 text-blue-600" />
                        {t('shipments.route_preview')}
                      </h4>
                      {getShipmentTypeBadge(shipment)}
                    </div>
                    <div className="bg-white p-4 rounded-lg border border-blue-100 shadow-sm">
                      <div className="text-lg font-semibold text-gray-800 text-center">
                        {(() => {
                          const validCities = shipment.routeCities.filter(city => city.city);
                          
                          if (validCities.length === 0) {
                            return 'N/A';
                          }
                          
                          if (validCities.length === 1) {
                            return getCityName(validCities[0].city);
                          }
                          
                          // Get origin (first city)
                          const originCity = getCityName(validCities[0].city);
                          
                          // Get destination cities (excluding origin)
                          const destinationCities = validCities.slice(1).map(city => getCityName(city.city));
                          
                          if (destinationCities.length === 0) {
                            return originCity;
                          }
                          
                          if (destinationCities.length === 1) {
                            return `${originCity} ${i18n.language === 'ar' ? '←' : '→'} ${destinationCities[0]}`;
                          }
                          
                          // Multiple destination cities - format as "Origin → City1 - City2 - City3"
                          // If there are 3 or more destination cities, exclude the first destination city
                          let citiesToShow = destinationCities;
                          if (destinationCities.length >= 3) {
                            citiesToShow = destinationCities.slice(1); // Skip first destination city
                          }
                          
                          const destinationDisplay = citiesToShow.join(' - ');
                          return `${originCity} ${i18n.language === 'ar' ? '←' : '→'} ${destinationDisplay}`;
                        })()}
                      </div>
                      <div className="text-sm text-blue-600 text-center mt-2 font-medium">
                        {(() => {
                          const validCities = shipment.routeCities.filter(city => city.city);
                          const destinationCities = validCities.slice(1);
                          const cityCount = destinationCities.length;
                          
                          if (cityCount === 0) return t('shipments.single_city', 'Single City');
                          if (cityCount === 1) return `${cityCount} ${t('shipments.destination_city', 'Destination')}`;
                          if (cityCount === 2) return `${cityCount} ${t('shipments.destination_cities_dual', 'Destinations')}`;
                          return `${cityCount} ${t('shipments.destination_cities_plural', 'Destinations')}`;
                        })()}
                      </div>
                    </div>
                  </div>

                  {/* Detailed Route List */}
                  <div className="space-y-3">
                    <h4 className="text-base font-semibold text-gray-900 flex items-center">
                      <TruckIcon className="w-4 h-4 mr-2 text-green-600" />
                      {t('shipments.route_stops')}
                    </h4>
                    <div className="grid gap-3">
                      {shipment.routeCities
                        .filter(city => city.city)
                        .map((city, index) => (
                          <div key={city.id || index} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
                            <div className="flex items-start gap-3">
                              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-white text-sm font-bold shadow-sm">
                                {index + 1}
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <h5 className="text-base font-semibold text-gray-900">
                                    {getCityName(city.city)}
                                  </h5>
                                  {city.site && (
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                      📍 {city.site}
                                    </span>
                                  )}
                                </div>
                                
                                {/* Stops for this city */}
                                {city.stops && city.stops.length > 0 && (
                                  <div className="mt-3 pl-4 border-l-2 border-green-200 bg-green-50 rounded-r-md p-3">
                                    <div className="flex items-center gap-2 mb-2">
                                      <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                                      <span className="text-xs font-medium text-green-800">
                                        {t('shipments.stops')} ({city.stops.length})
                                      </span>
                                    </div>
                                    <div className="space-y-1.5">
                                      {city.stops.map((stop, stopIndex) => (
                                        <div key={stop.id || stopIndex} className="flex items-center gap-2 bg-white p-2 rounded border border-green-100">
                                          <div className="w-4 h-4 bg-green-500 text-white rounded-full flex items-center justify-center text-xs font-bold">
                                            {stopIndex + 1}
                                          </div>
                                          <span className="text-xs font-medium text-gray-800">{stop.name}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                </div>
              ) : shipment.stops && shipment.stops.length > 0 ? (
                // Legacy stops-based display
                <div className="space-y-4">
                  {/* Route Summary */}
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 p-6 rounded-xl shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-lg font-semibold text-gray-900 flex items-center">
                        <MapPinIcon className="w-5 h-5 mr-2 text-blue-600" />
                        {t('shipments.route_preview')}
                      </h4>
                      {getShipmentTypeBadge(shipment)}
                    </div>
                    <div className="bg-white p-4 rounded-lg border border-blue-100 shadow-sm">
                      <div className="text-lg font-semibold text-gray-800 text-center">
                        {shipment.stops.map((stop, index) => {
                          const cityName = getCityName(stop.city);
                          const typeIcon = stop.type === 'pickup' ? '📦' : stop.type === 'delivery' ? '🚚' : '📦🚚';
                          return `${typeIcon} ${cityName}`;
                        }).join(i18n.language === 'ar' ? ' ← ' : ' → ')}
                      </div>
                      <div className="text-sm text-blue-600 text-center mt-2 font-medium">
                        {shipment.stops.length} {shipment.stops.length === 1 ? t('shipments.stop') : t('shipments.stops')}
                      </div>
                    </div>
                  </div>

                  {/* Detailed Stops List */}
                  <div className="space-y-3">
                    <h4 className="text-sm font-medium text-gray-900">{t('shipments.route_stops')}</h4>
                    {shipment.stops.map((stop, index) => (
                      <div key={stop.id || index} className="border rounded-lg p-4 bg-white">
                        <div className="flex items-start gap-3">
                          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-800 text-sm font-medium">
                            {index + 1}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                stop.type === 'pickup' 
                                  ? 'bg-green-100 text-green-800' 
                                  : stop.type === 'delivery'
                                  ? 'bg-blue-100 text-blue-800'
                                  : 'bg-purple-100 text-purple-800'
                              }`}>
                                {stop.type === 'pickup' ? '📦 ' + t('shipments.pickup') : 
                                 stop.type === 'delivery' ? '🚚 ' + t('shipments.delivery') : 
                                 '📦🚚 ' + t('shipments.both')}
                              </span>
                            </div>
                            <div className="font-medium text-gray-900">
                              {getCityName(stop.city)} - {stop.siteName}
                            </div>
                            {stop.address && (
                              <div className="text-sm text-gray-600 mt-1">{stop.address}</div>
                            )}
                            {stop.notes && (
                              <div className="text-sm text-gray-500 mt-1 italic">{stop.notes}</div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                // Legacy display for backward compatibility
                <div className="space-y-6">
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">{t('shipments.origin')}</h4>
                    <p className="text-gray-700">{getCityName(shipment.originCity) || shipment.origin}</p>
                  </div>
                  
                  <div className="border-l-4 border-blue-500 pl-4">
                    <div className="flex items-center space-x-2">
                      <TruckIcon className="w-5 h-5 text-blue-500" />
                      <span className="text-sm text-gray-600 font-medium">{t(`shipments.${shipment.status}`)}</span>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">{t('shipments.destination')}</h4>
                    <p className="text-gray-700">{getCityName(shipment.destinationCity) || shipment.destination}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Driver & Vehicle Assignment */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-semibold flex items-center">
                <TruckIcon className="w-5 h-5 mr-2" />
                {t('shipments.vehicle_driver_assignment')}
              </h3>
            </div>
            <div className="card-body space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <h4 className="font-medium text-gray-900 mb-2">{t('shipments.assigned_driver')}</h4>
                  <div>
                    <label className="text-sm font-medium text-gray-600">{t('shipments.driver_name', 'Driver Name')}</label>
                    <p className="text-gray-900 font-medium">
                      {(() => {
                        if (shipment.driverId) {
                          const driver = getDriverInfo(shipment.driverId);
                          if (driver) {
                            return driver.nickname && driver.nickname.trim() ? driver.nickname : driver.name;
                          }
                        }
                        return shipment.driverName && shipment.driverName !== 'Unknown Driver' 
                          ? shipment.driverName 
                          : t('common.not_assigned', 'Not assigned');
                      })()}
                    </p>
                  </div>
                  {shipment.driverId && (
                    <div>
                      <label className="text-sm font-medium text-gray-600">{t('drivers.driver_id', 'Driver ID')}</label>
                      <p className="text-gray-900 text-sm">
                        {(() => {
                          if (shipment.driverId) {
                            const driver = getDriverInfo(shipment.driverId);
                            if (driver && driver.driverCode) {
                              return driver.driverCode;
                            }
                          }
                          return shipment.driverId;
                        })()}
                      </p>
                    </div>
                  )}
                </div>
                <div className="space-y-3">
                  <h4 className="font-medium text-gray-900 mb-2">{t('shipments.assigned_vehicle')}</h4>
                  <div>
                    <label className="text-sm font-medium text-gray-600">{t('vehicles.plate_number', 'License Plate')}</label>
                    <p className="text-gray-900 font-medium">
                      {(() => {
                        // First check if the shipment has a direct vehiclePlate
                        if (shipment.vehiclePlate && shipment.vehiclePlate !== 'Unknown Vehicle') {
                          return shipment.vehiclePlate;
                        }
                        
                        // If not, try to find the vehicle by vehicleId
                        if (shipment.vehicleId) {
                          const vehicle = getVehicleInfo(shipment.vehicleId);
                          if (vehicle) {
                            return vehicle.plateNumber;
                          }
                        }
                        
                        return t('common.not_assigned', 'Not assigned');
                      })()}
                    </p>
                  </div>
                  {(shipment.vehicleId || shipment.isExternalDriver) && (
                    <div>
                      <label className="text-sm font-medium text-gray-600">{t('shipments.vehicle_details')}</label>
                      <p className="text-gray-900 text-sm">
                        {(() => {
                          // For external drivers, show the selected truck type
                          if (shipment.isExternalDriver) {
                            return getTruckTypeName(shipment.truckType) || t('common.vehicle_info');
                          }
                          
                          const vehicle = getVehicleInfo(shipment.vehicleId);
                          if (vehicle) {
                            return getTruckTypeName(vehicle.truckType || vehicle.type) || t('common.vehicle_info');
                          }
                          return shipment.vehicleId;
                        })()}
                      </p>
                    </div>
                  )}
                  {(shipment.truckType || (shipment.vehicleId && getVehicleInfo(shipment.vehicleId)?.truckType)) && (
                    <div>
                      <label className="text-sm font-medium text-gray-600">{t('shipments.truck_type')}</label>
                      <p className="text-gray-900 text-sm">
                        {(() => {
                          const truckType = shipment.truckType || getVehicleInfo(shipment.vehicleId)?.truckType;
                          if (!truckType) return t('common.not_specified', 'Not specified');
                          
                          // Try to find the truck type label from our predefined types
                          const truckTypeObj = truckTypes.find(tt => tt.value === truckType);
                          if (truckTypeObj && truckTypeObj.label) {
                            return truckTypeObj.label[i18n.language] || truckTypeObj.label.en;
                          }
                          
                          // Return the raw truck type if no match found
                          return truckType;
                        })()}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Cargo Details */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-semibold flex items-center">
                <DocumentTextIcon className="w-5 h-5 mr-2" />
                {t('shipments.cargo_details')}
              </h3>
            </div>
            <div className="card-body space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-600">{t('shipments.cargo_description')}</label>
                <p className="text-gray-900">{shipment.cargoDescription || t('common.not_specified', 'Not specified')}</p>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-600">{t('shipments.weight')}</label>
                  <p className="text-gray-900">
                    {shipment.cargoWeight ? `${shipment.cargoWeight} kg` : t('common.not_specified', 'Not specified')}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">{t('shipments.declared_value')}</label>
                  <p className="text-gray-900">
                    {shipment.totalValue ? `$${shipment.totalValue}` : t('common.not_specified', 'Not specified')}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">{t('shipments.shipment_price')}</label>
                  <p className="text-gray-900 font-semibold text-green-600">
                    {(() => {
                      const subtotal = calculateSubtotalBeforeTax(shipment);
                      return subtotal > 0 ? `SAR ${subtotal.toFixed(2)}` : t('common.not_specified', 'Not specified');
                    })()}
                  </p>
                </div>
              </div>
              {shipment.notes && (
                <div>
                  <label className="text-sm font-medium text-gray-600">{t('shipments.special_instructions')}</label>
                  <p className="text-gray-900">{shipment.notes}</p>
                </div>
              )}
            </div>
          </div>

          {/* Costing Information */}
          {(costing || shipment.commissionAmount) && (
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-semibold flex items-center">
                  <DocumentTextIcon className="w-5 h-5 mr-2" />
                  {t('shipments.costing_information', 'Costing Information')}
                </h3>
              </div>
              <div className="card-body space-y-4">
                {costing ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-3">
                      <h4 className="font-medium text-gray-900 mb-2">{t('costing.operational_costs', 'Operational Costs')}</h4>
                      <div className="space-y-2">
                        {costing.fuelCosts > 0 && (
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">{t('accounting.fuel_costs')}:</span>
                            <span className="text-sm font-medium">SAR {costing.fuelCosts.toLocaleString('en-US')}</span>
                          </div>
                        )}
                        {costing.tolls > 0 && (
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">{t('costing.tolls', 'Tolls')}:</span>
                            <span className="text-sm font-medium">SAR {costing.tolls.toLocaleString('en-US')}</span>
                          </div>
                        )}
                        {costing.maintenance > 0 && (
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">{t('accounting.expenseCategories.maintenance')}:</span>
                            <span className="text-sm font-medium">SAR {costing.maintenance.toLocaleString('en-US')}</span>
                          </div>
                        )}
                        {costing.otherOperational > 0 && (
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">{t('costing.other_operational', 'Other Operational')}:</span>
                            <span className="text-sm font-medium">SAR {costing.otherOperational.toLocaleString('en-US')}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="space-y-3">
                      <h4 className="font-medium text-gray-900 mb-2">{t('costing.administrative_costs', 'Administrative Costs')}</h4>
                      <div className="space-y-2">
                        {costing.administrative > 0 && (
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">{t('costing.administrative', 'Administrative')}:</span>
                            <span className="text-sm font-medium">SAR {costing.administrative.toLocaleString('en-US')}</span>
                          </div>
                        )}
                        {costing.insurance > 0 && (
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">{t('accounting.expenseCategories.insurance')}:</span>
                            <span className="text-sm font-medium">SAR {costing.insurance.toLocaleString('en-US')}</span>
                          </div>
                        )}
                        {costing.otherAdmin > 0 && (
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">{t('costing.other_admin', 'Other Admin')}:</span>
                            <span className="text-sm font-medium">SAR {costing.otherAdmin.toLocaleString('en-US')}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : null}
                
                {/* Commission Information */}
                {shipment.commissionAmount && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <h4 className="font-medium text-gray-900 mb-3">{t('drivers.commission_information', 'Commission Information')}</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">{t('drivers.commission_amount', 'Commission Amount')}:</span>
                        <span className="text-sm font-semibold text-blue-600">
                          SAR {shipment.commissionAmount.toLocaleString('en-US')}
                        </span>
                      </div>
                      {shipment.commissionPaid !== undefined && (
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">{t('drivers.commission_status', 'Commission Status')}:</span>
                          <span className={`text-sm font-medium ${shipment.commissionPaid ? 'text-green-600' : 'text-red-600'}`}>
                            {shipment.commissionPaid ? t('drivers.commission_paid', t('common.paid')) : t('drivers.commission_unpaid', t('common.unpaid'))}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Costing Summary */}
                {costing && (
                  <div className="mt-4 pt-4 border-t border-gray-200 bg-gray-50 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-3">{t('common.summary')}</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">{t('costing.total_costs', 'Total Costs')}:</span>
                        <span className="text-sm font-medium">SAR {(costing.totalCosts || 0).toLocaleString('en-US')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">{t('accounting.revenue')}:</span>
                        <span className="text-sm font-medium">SAR {calculateSubtotalBeforeTax(shipment).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </div>
                      <div className="flex justify-between border-t pt-2">
                        <span className="text-sm font-semibold text-gray-900">{t('chart.profit_margin')}:</span>
                        <span className={`text-sm font-semibold ${
                          (calculateSubtotalBeforeTax(shipment) - (costing.totalCosts || 0)) >= 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          SAR {(calculateSubtotalBeforeTax(shipment) - (costing.totalCosts || 0)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                    </div>
                    
                    {shipment.costingUpdatedAt && (
                      <div className="mt-3 pt-2 border-t border-gray-300">
                        <p className="text-xs text-gray-500">
                          {t('costing.updated_on', 'Updated on')}: {' '}
                          {shipment.costingUpdatedAt.toDate ? 
                            shipment.costingUpdatedAt.toDate().toLocaleString('en-US') :
                            new Date(shipment.costingUpdatedAt).toLocaleString('en-US')
                          }
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Status Actions */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-semibold">{t('shipments.update_status', 'Update Status')}</h3>
            </div>
            <div className="card-body space-y-3">
              <button
                onClick={() => updateShipmentStatus('pending')}
                disabled={shipment.status === 'pending'}
                className="w-full text-sm disabled:opacity-50 bg-yellow-500 hover:bg-yellow-600 text-white font-medium py-2 px-4 rounded-md transition-colors duration-200"
              >
                {t('shipments.mark_as_pending', 'Mark as Pending')}
              </button>
              <button
                onClick={() => updateShipmentStatus('in_transit')}
                disabled={shipment.status === 'in_transit'}
                className="w-full text-sm disabled:opacity-50 bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded-md transition-colors duration-200"
              >
                {t('shipments.mark_in_transit', 'Mark in Transit')}
              </button>
              <button
                onClick={() => updateShipmentStatus('delivered')}
                disabled={shipment.status === 'delivered'}
                className="w-full text-sm disabled:opacity-50 bg-green-500 hover:bg-green-600 text-white font-medium py-2 px-4 rounded-md transition-colors duration-200"
              >
                {t('shipments.mark_as_delivered', 'Mark as Delivered')}
              </button>
              <button
                onClick={() => updateShipmentStatus('canceled')}
                disabled={shipment.status === 'canceled'}
                className="w-full text-sm disabled:opacity-50 bg-red-500 hover:bg-red-600 text-white font-medium py-2 px-4 rounded-md transition-colors duration-200"
              >
                {t('shipments.cancel_shipment', 'Cancel Shipment')}
              </button>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-semibold">{t('shipments.quick_actions', 'Quick Actions')}</h3>
            </div>
            <div className="card-body space-y-3">
              <button
                onClick={() => window.print()}
                className="w-full text-sm bg-gray-500 hover:bg-gray-600 text-white font-medium py-2 px-4 rounded-md transition-colors duration-200"
              >
                {t('shipments.print_details', 'Print Details')}
              </button>
              <button
                onClick={() => navigate(`/shipments/${id}/edit`)}
                className="w-full text-sm bg-gray-500 hover:bg-gray-600 text-white font-medium py-2 px-4 rounded-md transition-colors duration-200"
              >
                {t('shipments.edit_shipment', 'Edit Shipment')}
              </button>
            </div>
          </div>

          {/* Summary */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-semibold">{t('shipments.summary', 'Summary')}</h3>
            </div>
            <div className="card-body space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">{t('shipments.status')}:</span>
                <span className="font-medium capitalize">{t(`shipments.${shipment.status}`)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">{t('shipments.priority')}:</span>
                <span className="font-medium capitalize">{t(`shipments.priority_${shipment.priority}`, shipment.priority)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">{t('shipments.service')}:</span>
                <span className="font-medium capitalize">{t(`shipments.service_${shipment.serviceType}`, shipment.serviceType)}</span>
              </div>
              {shipment.cost && (
                <div className="pt-2 border-t">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-600 font-medium">{t('shipments.pricing_breakdown', 'Pricing Breakdown')}:</span>
                  </div>
                  {(() => {
                    const totalPrice = parseFloat(shipment.cost);
                    const baseShipmentPrice = parseFloat(shipment.shipmentPrice || shipment.cost);
                    const backhaulPrice = parseFloat(shipment.backhaulPrice || 0);
                    const itemizedTotal = (shipment.itemizedCosts || []).reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);
                    
                    let mainBasePrice, mainVatAmount;
                    let backhaulVatAmount = backhaulPrice * 0.15;
                    let itemizedVatAmount = itemizedTotal * 0.15;

                    if (shipment.isVatIncluded) {
                      // Main price includes VAT, backhaul and itemized costs are without VAT
                      mainBasePrice = baseShipmentPrice / 1.15;
                      mainVatAmount = baseShipmentPrice - mainBasePrice;
                    } else {
                      // All prices are without VAT
                      mainBasePrice = baseShipmentPrice;
                      mainVatAmount = baseShipmentPrice * 0.15;
                    }

                    const totalBasePrice = mainBasePrice + backhaulPrice + itemizedTotal;
                    const totalVatAmount = mainVatAmount + backhaulVatAmount + itemizedVatAmount;
                    const grandTotal = totalBasePrice + totalVatAmount;

                    return (
                      <div className="space-y-2 text-sm bg-gray-50 p-3 rounded-lg">
                        {/* Base Shipment Price */}
                        <div className="flex justify-between">
                          <span className="text-gray-600">{t('shipments.base_shipment_price', 'Base Shipment')}:</span>
                          <span className="font-medium">SAR {mainBasePrice.toFixed(2)}</span>
                        </div>
                        
                        {/* Itemized Services Details */}
                        {shipment.itemizedCosts && shipment.itemizedCosts.length > 0 && (
                          <div className="ml-2 space-y-1 border-l-2 border-blue-200 pl-2">
                            <div className="text-xs font-medium text-blue-700 mb-1">
                              {t('shipments.additional_services', 'Additional Services')}:
                            </div>
                            {shipment.itemizedCosts.map((item, index) => (
                              <div key={index} className="flex justify-between text-xs">
                                <span className="text-gray-500 truncate max-w-[120px]" title={item.description}>
                                  {item.description} ({item.quantity}x)
                                </span>
                                <span className="font-medium">SAR {(parseFloat(item.total) || 0).toFixed(2)}</span>
                              </div>
                            ))}
                            <div className="flex justify-between text-xs font-medium border-t border-blue-200 pt-1">
                              <span className="text-blue-700">{t('shipments.services_subtotal', 'Services Total')}:</span>
                              <span className="text-blue-700">SAR {itemizedTotal.toFixed(2)}</span>
                            </div>
                          </div>
                        )}
                        
                        {/* Backhaul Details */}
                        {backhaulPrice > 0 && (
                          <div className="ml-2 space-y-1 border-l-2 border-green-200 pl-2">
                            <div className="text-xs font-medium text-green-700 mb-1">
                              {t('shipments.backhaul', 'Back Haul')}:
                            </div>
                            <div className="flex justify-between text-xs">
                              <span className="text-gray-500 truncate max-w-[120px]" title={shipment.backhaulDescription}>
                                {shipment.backhaulDescription || t('shipments.backhaul_service', 'Backhaul Service')}
                              </span>
                              <span className="font-medium">SAR {backhaulPrice.toFixed(2)}</span>
                            </div>
                          </div>
                        )}
                        
                        {/* Totals */}
                        <div className="border-t border-gray-300 pt-2 space-y-1">
                          <div className="flex justify-between">
                            <span className="text-gray-600">{t('shipments.subtotal_before_vat', 'Subtotal')}:</span>
                            <span className="font-medium">SAR {totalBasePrice.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">{t('shipments.vat_amount', 'VAT (15%)')}:</span>
                            <span className="font-medium">SAR {totalVatAmount.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between font-semibold text-green-600 border-t border-gray-400 pt-1">
                            <span>{t('shipments.total_price', t('common.total'))}:</span>
                            <span>SAR {grandTotal.toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
              {shipment.totalValue && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">{t('shipments.declared_value')}:</span>
                  <span className="font-medium text-gray-900">${shipment.totalValue}</span>
                </div>
              )}
              {/* Costing Information in Summary */}
              {costing && (
                <>
                  <div className="flex justify-between text-sm pt-2 border-t">
                    <span className="text-gray-600">{t('costing.total_costs', 'Total Costs')}:</span>
                    <span className="font-medium text-orange-600">SAR {(costing.totalCosts || 0).toLocaleString('en-US')}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">{t('chart.profit_margin')}:</span>
                    <span className={`font-semibold ${
                      (calculateSubtotalBeforeTax(shipment) - (costing.totalCosts || 0)) >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      SAR {(calculateSubtotalBeforeTax(shipment) - (costing.totalCosts || 0)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                </>
              )}
              {/* Commission Amount in Summary */}
              {shipment.commissionAmount && (
                <div className="flex justify-between text-sm pt-2 border-t">
                  <span className="text-gray-600">{t('drivers.commission_amount', 'Commission Amount')}:</span>
                  <span className="font-medium text-blue-600">SAR {shipment.commissionAmount.toLocaleString('en-US')}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Bill of Lading Modal */}
      {showBillOfLadingModal && (
        <BillOfLadingModal
          shipment={shipment}
          onClose={() => setShowBillOfLadingModal(false)}
        />
      )}
    </div>
  );
};

export default ShipmentDetail; 